/* eslint-disable react/no-multi-comp */
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import {
  AddressElement,
  Elements,
  LinkAuthenticationElement,
  useElements,
} from '@stripe/react-stripe-js'
import {
  loadStripe,
  type StripeAddressElementChangeEvent,
  type StripeLinkAuthenticationElementChangeEvent,
} from '@stripe/stripe-js'
import classNames from 'classnames'

import type { OfferPaymentFirstStepFormProps } from 'Modules/purchase/components/OfferPaymentForm/OfferPaymentFirstStepForm.types'
import type { ClientSecretResponse } from 'Modules/purchase/pages/PurchaseDevice/PurchaseDevice.types'

import { Alert, FeedbackText, Heading, PrimaryButton } from 'Components/_theme'
import { Icon } from 'Components/Icon/Icon'
import { LoadingState } from 'Components/LoadingState/LoadingState'

import { fetchFromPublicApiUsingPartialPath } from 'Portal/src/utilities'

const PaymentFormContent = (props: OfferPaymentFirstStepFormProps) => {
  const { billingAddress, billingCountry, billingName, email, setInvoiceData } =
    props
  const elements = useElements()
  const intl = useIntl()
  const { uuid = '' } = useParams<{ uuid: string }>()
  const navigate = useNavigate()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formEmail, setFormEmail] =
    useState<StripeLinkAuthenticationElementChangeEvent['value']>()
  const [formAddress, setFormAddress] =
    useState<StripeAddressElementChangeEvent['value']>()
  const [stripeError, setStripeError] = useState<string | undefined>()
  const [isEmailElementReady, setIsEmailElementReady] = useState(false)
  const [isAddressElementReady, setIsAddressElementReady] = useState(false)

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setStripeError(undefined)

    if (!elements) {
      return
    }

    // Request client secret
    try {
      setIsSubmitting(true)

      // Validate form elements
      const elementsSubmit = await elements.submit()

      if (elementsSubmit?.error) {
        setStripeError(elementsSubmit.error.message)

        return
      }

      const payload = JSON.stringify({
        ...formEmail,
        ...formAddress,
      })

      const response =
        await fetchFromPublicApiUsingPartialPath<ClientSecretResponse>(
          `/payment-intent/${uuid}`,
          { body: payload, method: 'POST' },
        )

      if (response.recreate_offer) {
        navigate('/offer-recreated', { replace: true })

        return
      }

      if (response.error || !response?.invoice_data?.client_secret) {
        throw new Error('There was an error creating your payment intent.')
      }

      setInvoiceData(response?.invoice_data)
    } catch (error) {
      setStripeError(
        error instanceof Error
          ? error.message
          : intl.formatMessage({ id: 'ErrorSubmittingRequest' }),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const isStripeElementReady = isAddressElementReady && isEmailElementReady

  return (
    <>
      {!isStripeElementReady && (
        <div className="d-flex align-items-center justify-content-center flex-fill flex-column">
          <LoadingState />
        </div>
      )}
      <div
        className={classNames('OfferPaymentForm d-flex flex-fill flex-column', {
          'd-none': !isStripeElementReady,
        })}
      >
        <Heading as={3} className="mb-9" size={4}>
          <FormattedMessage id="Payment" />
        </Heading>

        <form>
          <LinkAuthenticationElement
            className="mb-0"
            onChange={event => setFormEmail(event.value)}
            onReady={() => setIsEmailElementReady(true)}
            options={{
              defaultValues: {
                email: email || '',
              },
            }}
          />

          <FeedbackText className="mb-4 p-0">
            <div className="d-flex align-items-center">
              <Icon className="me-1" icon={faInfoCircle} size={12} />
              <FormattedMessage id="OfferPaymentForm_email_feedback_text" />
            </div>
          </FeedbackText>

          <AddressElement
            className="mb-4"
            onChange={event => setFormAddress(event.value)}
            onReady={() => setIsAddressElementReady(true)}
            options={{
              allowedCountries: [billingCountry],
              autocomplete: {
                apiKey: window.STRIPE_AUTOCOMPLETE_API_KEY,
                mode: 'google_maps_api',
              },
              defaultValues: {
                address: {
                  city: billingAddress?.city || '',
                  country: billingCountry,
                  line1: billingAddress?.line1 || '',
                  line2: billingAddress?.line2 || '',
                  postal_code: billingAddress?.postal_code || '',
                  state: billingAddress?.state || '',
                },
                name: billingName || '',
              },
              mode: 'billing',
            }}
          />

          {stripeError && (
            <Alert className="mb-2" variant="warning">
              {stripeError}
            </Alert>
          )}

          <PrimaryButton
            className="mt-4 mb-4 w-100"
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            <FormattedMessage id="Next" />
          </PrimaryButton>
        </form>
      </div>
    </>
  )
}

/**
 * First step of the payment form wrapped with Stripe Elements
 */
export const OfferPaymentFirstStepForm = (
  props: OfferPaymentFirstStepFormProps,
) => {
  const stripePromise = loadStripe(window.STRIPE_PUBLISHABLE_KEY)
  const { amount, currency } = props

  return (
    <Elements
      options={{
        amount,
        currency,
        mode: 'payment',
      }}
      stripe={stripePromise}
    >
      <PaymentFormContent {...props} />
    </Elements>
  )
}
